import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FONT_OPTIONS, BUTTON_OPTIONS, fontCss, radiusFor } from "@/web/sections";
import { ImageField, Lbl } from "./FieldRenderer";

const PRESETS = [
  { label: "PAG", primary: "#002FA7", accent: "#FFD700" }, { label: "Nuit", primary: "#0A0A0A", accent: "#E60000" }, { label: "Forêt", primary: "#065F46", accent: "#FFD700" },
  { label: "Terre", primary: "#7C2D12", accent: "#F59E0B" }, { label: "Océan", primary: "#1E3A8A", accent: "#0EA5E9" }, { label: "Rubis", primary: "#B91C1C", accent: "#0A0A0A" },
  { label: "Prune", primary: "#581C87", accent: "#F472B6" }, { label: "Ardoise", primary: "#1E293B", accent: "#22D3EE" },
];

// Phase 82 — apparence (thème) + identité (nom, logo, SEO, réseaux)
export function DesignPanel({ site, onChange, webApi }) {
  const theme = site.theme || {};
  const brand = site.brand || {};
  const seo = site.seo || {};
  const social = site.social || {};
  const setTheme = (p) => onChange({ theme: { ...theme, ...p } });
  const setBrand = (p) => onChange({ brand: { ...brand, ...p } });
  const setSeo = (p) => onChange({ seo: { ...seo, ...p } });
  const setSocial = (p) => onChange({ social: { ...social, ...p } });

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="design-panel">
      <div className="bg-white border border-[#DEE2E6] p-5 space-y-4">
        <div className="font-display font-bold text-lg">Couleurs & style</div>
        <div>
          <Lbl>Palettes rapides</Lbl>
          <div className="flex flex-wrap gap-2 mt-1">
            {PRESETS.map((p) => (
              <button key={p.label} type="button" onClick={() => setTheme({ primary: p.primary, accent: p.accent })} title={p.label} data-testid={`preset-${p.label}`}
                      className={`flex items-center gap-1 p-1 border ${theme.primary === p.primary && theme.accent === p.accent ? "border-[#0A0A0A]" : "border-[#DEE2E6]"}`}>
                <span className="h-5 w-5" style={{ background: p.primary }} /><span className="h-5 w-5" style={{ background: p.accent }} />
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Color label="Couleur principale" value={theme.primary} onChange={(v) => setTheme({ primary: v })} testid="theme-primary" />
          <Color label="Couleur d'accent" value={theme.accent} onChange={(v) => setTheme({ accent: v })} testid="theme-accent" />
          <Color label="Fond de page" value={theme.bg} onChange={(v) => setTheme({ bg: v })} testid="theme-bg" />
          <Color label="Texte" value={theme.text} onChange={(v) => setTheme({ text: v })} testid="theme-text" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Lbl>Police</Lbl>
            <select value={theme.font || "moderne"} onChange={(e) => setTheme({ font: e.target.value })} className="h-9 w-full px-2 text-sm border border-[#DEE2E6] bg-white" data-testid="theme-font">
              {FONT_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select></div>
          <div className="space-y-1"><Lbl>Boutons</Lbl>
            <select value={theme.buttons || "sharp"} onChange={(e) => setTheme({ buttons: e.target.value })} className="h-9 w-full px-2 text-sm border border-[#DEE2E6] bg-white" data-testid="theme-buttons">
              {BUTTON_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select></div>
        </div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={theme.dark_hero !== false} onCheckedChange={(v) => setTheme({ dark_hero: v })} data-testid="theme-dark-hero" /> Bandeau d'accueil sombre</label>
        <div className="border border-[#DEE2E6] p-4" style={{ background: theme.bg, color: theme.text, fontFamily: fontCss(theme.font) }} data-testid="theme-preview">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: theme.primary }}>Aperçu</div>
          <div className="text-2xl font-black tracking-tight mt-1">{brand.name || "Nom du site"}</div>
          <p className="text-sm opacity-80 mt-1">{brand.tagline || "Votre phrase d'accroche apparaît ici."}</p>
          <button type="button" className="mt-3 px-4 py-2 text-xs font-bold text-white" style={{ background: theme.primary, borderRadius: radiusFor(theme.buttons) }}>Bouton principal</button>
          <button type="button" className="mt-3 ml-2 px-4 py-2 text-xs font-bold" style={{ background: theme.accent, borderRadius: radiusFor(theme.buttons) }}>Accent</button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-[#DEE2E6] p-5 space-y-3">
          <div className="font-display font-bold text-lg">Identité</div>
          <div className="space-y-1"><Lbl>Nom du site *</Lbl><Input value={brand.name || ""} onChange={(e) => setBrand({ name: e.target.value })} className="h-9 text-sm" data-testid="brand-name" /></div>
          <div className="space-y-1"><Lbl>Slogan</Lbl><Input value={brand.tagline || ""} onChange={(e) => setBrand({ tagline: e.target.value })} className="h-9 text-sm" data-testid="brand-tagline" /></div>
          <div className="space-y-1"><Lbl>Logo</Lbl><ImageField value={brand.logo_url} onChange={(v) => setBrand({ logo_url: v })} webApi={webApi} testid="brand-logo" /></div>
          <div className="space-y-1"><Lbl>Favicon (icône d'onglet)</Lbl><ImageField value={brand.favicon_url} onChange={(v) => setBrand({ favicon_url: v })} webApi={webApi} testid="brand-favicon" /></div>
        </div>
        <div className="bg-white border border-[#DEE2E6] p-5 space-y-3">
          <div className="font-display font-bold text-lg">Référencement (SEO)</div>
          <div className="space-y-1"><Lbl>Titre de la page (Google)</Lbl><Input value={seo.title || ""} onChange={(e) => setSeo({ title: e.target.value })} className="h-9 text-sm" maxLength={70} data-testid="seo-title" /><div className="text-[10px] text-[#868E96]">{(seo.title || "").length}/70</div></div>
          <div className="space-y-1"><Lbl>Description</Lbl><Textarea value={seo.description || ""} onChange={(e) => setSeo({ description: e.target.value })} rows={3} maxLength={160} className="text-sm" data-testid="seo-description" /><div className="text-[10px] text-[#868E96]">{(seo.description || "").length}/160</div></div>
          <div className="space-y-1"><Lbl>Image de partage (réseaux sociaux)</Lbl><ImageField value={seo.og_image_url} onChange={(v) => setSeo({ og_image_url: v })} webApi={webApi} testid="seo-og" /></div>
        </div>
        <div className="bg-white border border-[#DEE2E6] p-5 space-y-2">
          <div className="font-display font-bold text-lg">Réseaux sociaux</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {["facebook", "instagram", "linkedin", "tiktok", "youtube", "x", "website"].map((k) => (
              <div key={k} className="space-y-1"><Lbl>{k === "x" ? "X (Twitter)" : k === "website" ? "Site existant" : k[0].toUpperCase() + k.slice(1)}</Lbl>
                <Input value={social[k] || ""} onChange={(e) => setSocial({ [k]: e.target.value })} placeholder="https://…" className="h-9 text-sm" data-testid={`social-${k}`} /></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Color({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1"><Lbl>{label}</Lbl>
      <div className="flex gap-2"><Input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 p-1 cursor-pointer" data-testid={testid} /><Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 font-mono text-xs" /></div>
    </div>
  );
}
